import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { X } from "lucide-react";
import PhoneInput from "react-phone-number-input";
import {
  useGetGroupsQuery,
  useGetUserGroupsQuery,
  useUpdateMemberMutation,
  useGetDesignationsQuery,
} from "../../infrastructure/api/membersGroupsApi";
import { useGetRolesQuery } from "@/features/roles-permissions/infrastructure/api/RolesApi";
import type { Member } from "../../domain/entities/Member";

interface Props {
  open: boolean;
  onOpenChange: (val: boolean) => void;
  member: Member | null;
}

export const EditMemberDialog = ({ open, onOpenChange, member }: Props) => {
  const { t } = useTranslation("membersGroups");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState<string>("");
  const [roleId, setRoleId] = useState("");
  const [designationId, setDesignationId] = useState("");
  const [selectedGroups, setSelectedGroups] = useState<
    { id: string; name: string }[]
  >([]);
  const [groupsTouched, setGroupsTouched] = useState(false);
  const [groupSelectKey, setGroupSelectKey] = useState(0);

  // Pre-populate when member changes
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (member) {
      setName(member.name);
      setPhone(member.phone);
      setRoleId(member.role_id);
      setDesignationId(member.designation_id);
      setSelectedGroups([]);
      setGroupsTouched(false);
      setGroupSelectKey((prev) => prev + 1);
    }
  }, [member]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const { data: roles = [], isLoading: rolesLoading } = useGetRolesQuery(
    undefined,
    { skip: !open },
  );
  const { data: designations = [], isLoading: designationsLoading } =
    useGetDesignationsQuery(undefined, { skip: !open });
  const { data: groups = [], isLoading: groupsLoading } = useGetGroupsQuery(
    undefined,
    { skip: !open },
  );
  const {
    data: userGroups = [],
    isLoading: userGroupsLoading,
    isFetching: userGroupsFetching,
    isSuccess: userGroupsLoaded,
  } = useGetUserGroupsQuery(member?.id ?? "", {
    skip: !open || !member,
    refetchOnMountOrArgChange: true,
  });
  const [updateMember, { isLoading: updating }] = useUpdateMemberMutation();

  useEffect(() => {
    if (!open || groupsTouched || !userGroupsLoaded) return;
    setSelectedGroups(
      userGroups.map((group) => ({ id: group.id, name: group.name })),
    );
  }, [groupsTouched, open, userGroups, userGroupsLoaded]);

  const isValid =
    name.trim() &&
    phone &&
    roleId &&
    designationId &&
    userGroupsLoaded &&
    !userGroupsLoading &&
    !userGroupsFetching;

  const addGroup = (groupId: string) => {
    const group = groups.find((g) => g.id === groupId);
    if (group && !selectedGroups.some((g) => g.id === groupId)) {
      setGroupsTouched(true);
      setSelectedGroups((prev) => [
        ...prev,
        { id: group.id, name: group.name },
      ]);
      setGroupSelectKey((prev) => prev + 1);
    }
  };

  const removeGroup = (groupId: string) => {
    setGroupsTouched(true);
    setSelectedGroups((prev) => prev.filter((g) => g.id !== groupId));
    setGroupSelectKey((prev) => prev + 1);
  };

  const handleClose = () => {
    onOpenChange(false);
  };

  const handleSave = async () => {
    if (!isValid || !member) return;
    try {
      await updateMember({
        user_id: member.id,
        name: name.trim(),
        phone,
        role_id: roleId,
        designation_id: designationId,
        group_ids: selectedGroups.map((g) => g.id),
      }).unwrap();
      handleClose();
    } catch {
      // errors handled silently; toast can be added here
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl border p-0 sm:max-w-[650px]">
        {/* Header */}
        <DialogHeader className="flex flex-row items-center justify-between px-6 py-4 border-b">
          <DialogTitle className="text-lg font-semibold">
            {t("editMemberDialog.title")}
          </DialogTitle>
        </DialogHeader>

        {/* Body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6">
          {/* Name */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editMemberDialog.nameLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Input
              placeholder={t("editMemberDialog.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          {/* Mobile */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editMemberDialog.phoneLabel")} <span className="text-destructive-text">*</span>
            </label>
            <div className="flex h-10 rounded-md border border-input overflow-hidden">
              <PhoneInput
                defaultCountry="IN"
                international
                countryCallingCodeEditable={false}
                placeholder={t("editMemberDialog.phonePlaceholder")}
                value={phone}
                onChange={(val) => setPhone(val ?? "")}
              />
            </div>
          </div>

          {/* Role */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editMemberDialog.roleLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Select
              value={roleId}
              onValueChange={setRoleId}
              disabled={rolesLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={rolesLoading ? t("editMemberDialog.loading") : t("editMemberDialog.selectRole")}
                />
              </SelectTrigger>
              <SelectContent>
                {roles.map((role) => (
                  <SelectItem key={role.id} value={role.id}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Designation */}
          <div className="space-y-1">
            <label className="text-sm font-medium">
              {t("editMemberDialog.designationLabel")} <span className="text-destructive-text">*</span>
            </label>
            <Select
              value={designationId}
              onValueChange={setDesignationId}
              disabled={designationsLoading}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    designationsLoading ? t("editMemberDialog.loading") : t("editMemberDialog.selectDesignation")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {designations.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Groups (multi-select) */}
          <div className="space-y-1">
            <label className="text-sm font-medium">{t("editMemberDialog.groupsLabel")}</label>
            <Select
              key={groupSelectKey}
              onValueChange={addGroup}
              disabled={groupsLoading || userGroupsLoading || userGroupsFetching}
            >
              <SelectTrigger className="w-full">
                <SelectValue
                  placeholder={
                    groupsLoading || userGroupsLoading || userGroupsFetching
                      ? t("editMemberDialog.loading")
                      : t("editMemberDialog.addToGroup")
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {selectedGroups.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {selectedGroups.map((group) => (
                  <div
                    key={group.id}
                    className="flex items-center gap-1 rounded-md bg-[color-mix(in_oklab,var(--primary)_12%,white)] px-2 py-1 text-sm text-black"
                  >
                    {group.name}
                    <button type="button" onClick={() => removeGroup(group.id)}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <DialogFooter className="flex h-20 shrink-0 justify-end gap-3 border-t px-6 py-4">
          <Button
            variant="outline"
            className="border-primary text-primary cursor-pointer hover:bg-white hover:text-primary"
            onClick={handleClose}
            disabled={updating}
          >
            {t("editMemberDialog.cancel")}
          </Button>

          <Button
            className="bg-primary text-white hover:bg-primary cursor-pointer"
            onClick={handleSave}
            disabled={!isValid}
            loading={updating}
          >
            {t("editMemberDialog.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
